import React, { useCallback } from 'react';
import { gql } from '@apollo/react-hooks';
import DataGrid from '#/common/components/DataGrid';
import type { ActionFn, SetIsLoading } from '#/common/components/DataGrid';
import { getAssetPlanList } from '#/graphql/queries';
import type { AssetPlan } from '#/graphql/types';
import useToast from '#/common/hooks/useToast';
import columns from './columns';
import { actions } from './actions';
import useDeleteAssetPlanMutation from '../../hooks/useDeleteAssetPlanMutation';

const getAssetPlanListQuery = gql(getAssetPlanList);

const AssetPlanDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { deleteAssetPlan } = useDeleteAssetPlanMutation();

  const deleteItems = useCallback(
    async (selectedItems: AssetPlan[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!selectedItems || selectedItems.length === 0) {
        return;
      }

      const sure = await confirm(
        'error',
        `Are you sure you want to delete ${selectedItems.length} asset plan${selectedItems.length > 1 ? 's' : ''}?`,
      );

      if (!sure) {
        return;
      }

      setIsLoading?.(true, 'Deleting data...');
      const idsToDelete = selectedItems.map((item) => item.id);
      await deleteAssetPlan(idsToDelete.join(','));
    },
    [confirm, deleteAssetPlan],
  );

  const assetPlanSelectedActions: ActionFn<AssetPlan>[] = [
    {
      key: 'delete-asset-plans',
      icon: 'trash',
      text: 'Delete',
      onClick: async (e) => {
        await deleteItems(e.selectedItems, { setIsLoading: e.setIsLoading });
        e?.setIsLoading?.(false);
        e.dg?.instance.clearSelection();
        e.dg?.instance.refresh();
      },
    },
  ];

  return (
    <DataGrid<AssetPlan>
      id="asset-plans"
      pluralText="Asset Plans"
      singularText="Asset Plan"
      columns={columns}
      actions={actions}
      selectedActions={assetPlanSelectedActions}
      recordUrl="/asset-plans/:id/items"
      getItemsQuery={getAssetPlanListQuery}
      selectionMode="multiple"
    />
  );
};

export default React.memo(AssetPlanDataGrid);
